import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  bulkUpdateShopOrderCashReceipt,
  getShopOrderCashReceiptCancelPending,
  getShopOrderCashReceiptPending,
} from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderCashReceiptStampedUrl } from '@shared/api/shopOrder/shopOrder.hook';
import {
  ShopOrderCashReceiptCancelPendingItem,
  ShopOrderCashReceiptCancelPendingResponse,
  ShopOrderCashReceiptPendingItem,
  ShopOrderCashReceiptPendingResponse,
  ShopOrderCashReceiptSourceKind,
  ShopOrderCashReceiptStampedRow,
  ShopOrderCashReceiptStatus,
  ShopOrderCashReceiptTier,
} from '@shared/api/shopOrder/shopOrder.interface';
import { CASH_RECEIPT_SELF_ISSUE_METHOD } from '@shared/api/shopOrder/shopOrder.utils';
import { Loading } from '@shared/components/Loading';
import { api_ } from '@shared/plugIn/axios';
import { toastError, toastSuccess } from '@shared/plugIn/toast';
import { useAuth } from '@shared/state/hooks/useAuth';
import { PaymentState } from '@shared/types';
import { formatPrice } from '@shared/utils/formatPrice';
import { paymentStatusBadge } from '@shared/utils/orderStatusLabel';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import { useSellerTopNavHook } from '../hooks/useSellerTopNavHook';
import { exportCashReceiptQueueExcel } from '../utils/cashReceiptQueueExcel';

/**
 * 현금영수증 관리 (관리자 전용)
 *
 * PG를 거치지 않은 '계좌 입금' 건은 **아무도 대신 현금영수증을 발행해주지 않는다.**
 * 지금까지 은행 입금기록을 눈으로 주문에 이어붙여 홈택스에 직접 쳤고, 시스템엔 발행
 * 여부가 하나도 안 남았다 — 발행했는지 확인할 수도, 누락을 알아챌 수도 없었다.
 *
 * 이 화면이 하는 일은 **실행이 아니라 목록과 도장**이다. 홈택스 실행은 계속 사람이 한다.
 *  - 발행 대기: 대상 조건 ∧ 도장 없음. 오래된 것부터(서버 고정) — 누락 방지가 존재 이유다
 *  - 취소 대기: 주문이 취소돼 서버가 자동으로 내려놓은 건. 돈은 돌려줬는데 홈택스엔
 *    매출이 남아있는 상태라, 홈택스에서 지우고 취소 완료 도장을 찍는다
 *  - 처리 기록: 도장이 찍힌 건. 잘못 누른 도장 되돌리기와 자동 전환 누락 감지
 *
 * (스펙: docs/shop-order-cash-receipt-spec.md · 백엔드 shop-order.cash-receipt.ts)
 */

const PAGE_LIMIT = 200; // 서버 최대치. 월 물량(~120건)이 한 페이지에 다 들어온다
const BULK_MAX = 500; // 서버 제약 — 한 번에 도장 찍을 수 있는 최대 건수

/**
 * 처리 기한 — 안전 5일 / 최대 30일. **판정도 구간별 집계도 서버가 한다**
 * (`thresholds` · `items[].tier` · `safeCount`/`lateCount`/`overCount`).
 *
 * 아래 상수·함수는 서버가 그 값을 안 줄 때만 쓰는 **폴백**이다 — 배포 시차로 구버전
 * 응답이 오면 화면이 기한을 통째로 잃는 것보다 낫다. 경계는 서버와 같게 맞춰뒀다:
 * 5일째까지 safe, 6~30일 late, 31일째부터 over.
 */
const DEFAULT_SAFE_DAYS = 5;
const DEFAULT_LIMIT_DAYS = 30;

type DeadlineLevel = ShopOrderCashReceiptTier;

const TIER_STYLE: Record<DeadlineLevel, { className: string; rowClassName: string }> = {
  safe: { className: 'bg-emerald-50 text-emerald-700', rowClassName: '' },
  late: { className: 'bg-amber-100 text-amber-800', rowClassName: 'bg-amber-50/40' },
  over: { className: 'bg-red-100 text-red-700', rowClassName: 'bg-red-50/50' },
};

const tierOf = (
  waitingDays: number,
  safeDays: number,
  limitDays: number,
): DeadlineLevel => {
  if (waitingDays <= safeDays) return 'safe';
  return waitingDays <= limitDays ? 'late' : 'over';
};

//* 배지 문구 — 남은 날짜로 말해준다. 표·엑셀이 같은 규칙을 보게 이 함수 하나만 쓴다.
const deadlineOf = (
  waitingDays: number,
  thresholds: { safeDays: number; limitDays: number },
  tier?: DeadlineLevel,
) => {
  const { safeDays, limitDays } = thresholds;
  const level = tier ?? tierOf(waitingDays, safeDays, limitDays);
  //* 서버가 waitingDays를 빠뜨려도 'D-NaN'을 찍지 않는다. 기한을 모를 땐 모른다고 쓴다 —
  //* 0으로 채우면 한참 지난 건이 '기한 내'로 보여 누락 방지라는 이 화면의 목적을 배신한다.
  if (!Number.isFinite(waitingDays)) {
    return { level, label: '기한 미상', sub: '', ...TIER_STYLE[level] };
  }
  const safeLeft = safeDays - waitingDays;
  const limitLeft = limitDays - waitingDays;
  const label =
    level === 'safe'
      ? safeLeft === 0
        ? '오늘까지'
        : `D-${safeLeft}`
      : level === 'late'
      ? `${-safeLeft}일 지연`
      : `최대기한 +${-limitLeft}일`;
  const sub =
    level === 'late'
      ? limitLeft === 0
        ? '최대기한 오늘'
        : `최대기한 D-${limitLeft}`
      : `결제 ${waitingDays}일차`;
  return { level, label, sub, ...TIER_STYLE[level] };
};

const DEADLINE_FILTER_LABEL: Record<DeadlineLevel, string> = {
  safe: '기한 내',
  late: '안전기한 초과',
  over: '최대기한 초과',
};

//* 발행액이 어디서 왔는지 — itemChange 행에서 결제총액을 치면 이중 발행이 난다
const KIND_BADGE: Record<
  ShopOrderCashReceiptSourceKind,
  { label: string; className: string }
> = {
  deposit: { label: '계좌입금', className: 'bg-gray-100 text-gray-600' },
  itemChange: { label: '교체차액', className: 'bg-violet-100 text-violet-700' },
};

//* 기한일 = 결제일 + n일. 홈택스에 칠 때 "언제까지"를 날짜로 보여주려고 쓴다
const deadlineDate = (purchasedAt: Date | string | undefined, days: number) => {
  if (!purchasedAt) return undefined;
  const d = new Date(purchasedAt);
  d.setDate(d.getDate() + days);
  return d;
};

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const pad = (n: number) => String(n).padStart(2, '0');

//* KST 기준 'YYYY-MM'. 서버가 기간을 KST 하루 경계로 해석하므로 브라우저 시간대에
//* 기대지 않고 직접 9시간 더해 계산한다 (해외에서 열어도 같은 달을 가리키게).
const kstMonthKey = (monthOffset = 0) => {
  const kst = new Date(Date.now() + KST_OFFSET_MS);
  const y = kst.getUTCFullYear();
  const m = kst.getUTCMonth() + monthOffset;
  const shifted = new Date(Date.UTC(y, m, 1));
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}`;
};

//* 'YYYY-MM'(그 달 전체) 또는 'YYYY-MM-DD'를 KST 경계의 UTC 순간으로.
//* 처리 기록 탭은 서버의 기간 해석을 못 쓰고 직접 필터를 만들어야 해서 필요하다.
const kstBoundary = (text: string, edge: 'start' | 'end'): Date => {
  const ym = /^(\d{4})-(\d{1,2})$/.exec(text);
  if (ym) {
    const year = Number(ym[1]);
    const month = Number(ym[2]);
    const ms =
      edge === 'start' ? Date.UTC(year, month - 1, 1) : Date.UTC(year, month, 1) - 1;
    return new Date(ms - KST_OFFSET_MS);
  }
  const parsed = new Date(text);
  const kst = new Date(parsed.getTime() + KST_OFFSET_MS);
  if (edge === 'start') kst.setUTCHours(0, 0, 0, 0);
  else kst.setUTCHours(23, 59, 59, 999);
  return new Date(kst.getTime() - KST_OFFSET_MS);
};

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];

//* 한국식 날짜 표기 — 올해면 연도를 생략한다 ("8월 3일(월)").
//* 세무 화면이라 요일이 실질적으로 필요하다: 홈택스 처리가 평일에만 되기 때문.
const fmtDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const thisYear = new Date().getFullYear();
  const head = d.getFullYear() === thisYear ? '' : `${d.getFullYear()}년 `;
  return `${head}${d.getMonth() + 1}월 ${d.getDate()}일(${WEEKDAY[d.getDay()]})`;
};

//* 기간 안내처럼 연도가 빠지면 안 되는 자리 ("2026년 7월 1일")
const fmtDateFull = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
};

const fmtDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${fmtDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const monthLabel = (month: string) => {
  const [year, mm] = month.split('-');
  return `${year}년 ${Number(mm)}월`;
};

//* 월 선택 목록 — 하한선(cutoff)이 있는 달부터 이번 달까지, 최근 달이 위로.
//* input[type=month]는 브라우저 로케일에 따라 "2026-08"로 보여서 한국식으로 직접 만든다.
//*! cutoff는 axios 인터셉터가 되살린 Date지만, 되살리기 정규식이 `+09:00` 같은 양수
//*! 오프셋 표기는 안 잡는다 — 문자열로 남으면 .getTime()이 없어 화면 전체가 죽는다.
//*! 서버가 준 값에 Date 메서드를 직접 부르지 말고 항상 new Date()로 한 번 감쌀 것.
const monthOptions = (cutoff: Date | string | undefined): string[] => {
  const start = cutoff ? new Date(new Date(cutoff).getTime() + KST_OFFSET_MS) : undefined;
  const kstNow = new Date(Date.now() + KST_OFFSET_MS);
  const from = start
    ? { y: start.getUTCFullYear(), m: start.getUTCMonth() }
    : { y: kstNow.getUTCFullYear() - 1, m: kstNow.getUTCMonth() };
  const months: string[] = [];
  const cursor = new Date(Date.UTC(from.y, from.m, 1));
  const end = new Date(Date.UTC(kstNow.getUTCFullYear(), kstNow.getUTCMonth(), 1));
  while (cursor.getTime() <= end.getTime()) {
    months.push(`${cursor.getUTCFullYear()}-${pad(cursor.getUTCMonth() + 1)}`);
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return months.reverse();
};

const statusOf = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

const errorMessage = (e: unknown): string => {
  switch (statusOf(e)) {
    case 400:
      //* 서버 message는 400에서만 살아남는다 (전역 필터가 커스텀 코드를 덮어씀)
      return (
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '요청이 올바르지 않습니다.'
      );
    case 401:
    case 403:
      return '관리자만 처리할 수 있습니다.';
    case 404:
      return '아직 서버에 반영되지 않은 기능입니다.';
    default:
      return '처리에 실패했습니다.';
  }
};

const CASH_RECEIPT_STATUS_BADGE: Record<
  ShopOrderCashReceiptStatus,
  { label: string; className: string }
> = {
  [ShopOrderCashReceiptStatus.Issued]: {
    label: '발행완료',
    className: 'bg-emerald-100 text-emerald-800',
  },
  [ShopOrderCashReceiptStatus.CancelRequested]: {
    label: '취소요청',
    className: 'bg-amber-100 text-amber-800',
  },
  [ShopOrderCashReceiptStatus.Canceled]: {
    label: '취소완료',
    className: 'bg-gray-200 text-gray-700',
  },
};

const userNameOf = (
  user?: string | { _id: string; info?: { name?: string } },
): string | undefined => (typeof user === 'object' ? user?.info?.name : undefined);

//* 홈택스에 그대로 붙여넣을 수 있게 표기는 하이픈, 복사는 숫자 원문
const copyPhone = async (phone: string) => {
  try {
    await navigator.clipboard.writeText(phone);
    toastSuccess(`${phoneNumberStringFormator(phone)} 복사`);
  } catch {
    toastError('복사에 실패했습니다.');
  }
};

type Tab = 'pending' | 'cancelPending' | 'stamped';
type RangeState = { from?: string; to?: string };
//* 전환누락 = 발행 도장(`Issued`)인데 주문은 취소된 건. 지금은 서버가 취소 확정 시
//* 자동으로 `CancelRequested`로 내리므로 **정상이면 항상 0이어야 한다.**
//* 0이 아니면 자동 전환 이전에 취소됐거나 전환이 실패한 것 — 취소 대기 큐에 안 뜨므로
//* 여기서 잡아 수동으로 취소 요청 도장을 찍는다. (큐의 대체재가 아니라 그물이다)
type StampedFilter = 'all' | ShopOrderCashReceiptStatus | 'cancelNeeded';

const ALL_STAMPED_STATUSES = [
  ShopOrderCashReceiptStatus.Issued,
  ShopOrderCashReceiptStatus.CancelRequested,
  ShopOrderCashReceiptStatus.Canceled,
];

//* 우리가 발행한(또는 발행할) 금액 — 서버 cashReceiptAmountOf와 같은 규칙.
//* 큐는 서버가 amount로 내려주지만 처리 기록 탭은 제네릭 find라 프론트가 파생해야 한다.
//*! 여기서 결제총액을 그대로 쓰면 교체 차액 건의 발행액이 부풀려 보인다.
//* 결제수단 상수는 shopOrder.utils의 것을 그대로 쓴다 — 같은 문자열('계좌 입금')을 두 이름으로
//* 들고 있으면 한쪽만 고쳐졌을 때 kind 판정과 금액 파생이 조용히 어긋난다.
const stampedKindOf = (
  row: ShopOrderCashReceiptStampedRow,
): ShopOrderCashReceiptSourceKind =>
  row.paymentDetail?.method === CASH_RECEIPT_SELF_ISSUE_METHOD ? 'deposit' : 'itemChange';

const stampedAmountOf = (row: ShopOrderCashReceiptStampedRow): number =>
  stampedKindOf(row) === 'deposit'
    ? //* 회계모델 A라 교체 차액이 결제총액에 이미 가산돼 있다
      row.paymentDetail?.paymentByCash ?? 0
    : //* PG 결제 건은 계좌로 따로 받은 몫만 (원결제분은 PG가 발행)
      (row.orderDetail?.itemChanges ?? []).reduce(
        (sum, change) => sum + (change?.additionalPaymentByCash ?? 0),
        0,
      );

const isOrderCanceled = (status?: PaymentState) =>
  status === PaymentState.CANCELED || status === PaymentState.CANCEL_REQUESTED;

export const SellerCashReceiptPage: FC = function SellerCashReceiptPage() {
  useSellerTopNavHook('현금영수증');
  const { isAdmin } = useAuth();
  const admin = isAdmin();

  const [tab, setTab] = useState<Tab>('pending');
  const [range, setRange] = useState<RangeState>({}); // 기본 전체(하한선~지금)
  const [page, setPage] = useState(1);
  const [deadlineFilter, setDeadlineFilter] = useState<'all' | DeadlineLevel>('all');
  const [stampedFilter, setStampedFilter] = useState<StampedFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [working, setWorking] = useState(false);

  const [pending, setPending] = useState<ShopOrderCashReceiptPendingResponse | null>(
    null,
  );
  const [cancelQueue, setCancelQueue] =
    useState<ShopOrderCashReceiptCancelPendingResponse | null>(null);
  const [stamped, setStamped] = useState<ShopOrderCashReceiptStampedRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getShopOrderCashReceiptPending({
        ...range,
        page,
        limit: PAGE_LIMIT,
      });
      setPending(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setPending(null);
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [range, page]);

  //* 취소 대기 큐는 기간 파라미터가 없다 — 언제 결제된 건이든 취소는 즉시 처리 대상이다
  const loadCancelQueue = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getShopOrderCashReceiptCancelPending({ limit: PAGE_LIMIT });
      setCancelQueue(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setCancelQueue(null);
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStamped = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api_.get<ShopOrderCashReceiptStampedRow[]>(
        shopOrderCashReceiptStampedUrl(ALL_STAMPED_STATUSES, {
          from: range.from ? kstBoundary(range.from, 'start') : undefined,
          to: range.to ? kstBoundary(range.to, 'end') : undefined,
        }),
      );
      //*! 구서버는 스키마에 없는 필터를 조용히 버려 전 주문을 돌려준다 — 도장이 실제로
      //*! 찍힌 건만 남기는 이 한 줄이 그 오검을 막는다 (strictQuery 함정).
      setStamped(
        Array.isArray(data)
          ? data.filter((row) => !!row.selfCashReceiptDetail?.status)
          : [],
      );
      setError(null);
    } catch (err) {
      console.error(err);
      setStamped([]);
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    if (!admin) return;
    if (tab === 'pending') loadPending();
    else if (tab === 'cancelPending') loadCancelQueue();
    else loadStamped();
  }, [admin, tab, loadPending, loadCancelQueue, loadStamped]);

  //* 취소 대기 건수는 탭을 안 열어도 보여야 한다 — 돈은 돌려줬는데 홈택스엔 매출이
  //* 남아있는 상태라 방치되면 부가세 신고가 어긋난다. 첫 진입에 1회만 조회.
  useEffect(() => {
    if (!admin) return;
    getShopOrderCashReceiptCancelPending({ limit: 1 })
      .then((data) => setCancelQueue((prev) => prev ?? data))
      .catch(() => undefined);
  }, [admin]);

  //* 탭·기간·페이지가 바뀌면 선택을 비운다. 돈이 걸린 일괄 도장이라 "안 보이는 선택"이
  //* 남아 함께 찍히는 사고를 원천 차단한다 (선택 합계도 화면과 항상 일치하게 된다).
  useEffect(() => {
    setSelected(new Set());
  }, [tab, range, page]);

  const reload = () => {
    if (tab === 'pending') loadPending();
    else if (tab === 'cancelPending') loadCancelQueue();
    else loadStamped();
  };

  const applyRange = (next: RangeState) => {
    setRange(next);
    setPage(1);
  };

  //* 기한 경계는 서버 값이 정답 — 구버전 응답이면 폴백 상수로 화면을 살려둔다
  const thresholds = useMemo(
    () => ({
      safeDays: pending?.thresholds?.safeDays ?? DEFAULT_SAFE_DAYS,
      limitDays: pending?.thresholds?.limitDays ?? DEFAULT_LIMIT_DAYS,
    }),
    [pending],
  );

  const pendingItems = useMemo(() => {
    //* 계산서를 발행한 건(Done)은 서버 필터가 이미 뺀다(집계도 같은 필터라 숫자가 맞는다).
    //* 이 한 줄은 구서버 폴백 겸 방어 — 요청 대기(Requested)는 배지로만 경고하고 남긴다.
    const items = (pending?.items ?? []).filter(
      (item) => item.taxInvoiceStatus !== 'Done',
    );
    if (deadlineFilter === 'all') return items;
    return items.filter(
      (item) =>
        deadlineOf(item.waitingDays, thresholds, item.tier).level === deadlineFilter,
    );
  }, [pending, deadlineFilter, thresholds]);

  const stampedRows = useMemo(() => {
    const rows = stamped ?? [];
    switch (stampedFilter) {
      case 'all':
        return rows;
      case 'cancelNeeded':
        return rows.filter(
          (row) =>
            row.selfCashReceiptDetail?.status === ShopOrderCashReceiptStatus.Issued &&
            isOrderCanceled(row.paymentDetail?.status),
        );
      default:
        return rows.filter((row) => row.selfCashReceiptDetail?.status === stampedFilter);
    }
  }, [stamped, stampedFilter]);

  const cancelItems = useMemo(() => cancelQueue?.items ?? [], [cancelQueue]);

  const visibleIds = useMemo(() => {
    if (tab === 'pending') return pendingItems.map((item) => item._id);
    if (tab === 'cancelPending') return cancelItems.map((item) => item._id);
    return stampedRows.map((row) => row._id);
  }, [tab, pendingItems, cancelItems, stampedRows]);

  const selectedAmount = useMemo(() => {
    if (tab === 'pending') {
      return pendingItems
        .filter((item) => selected.has(item._id))
        .reduce((sum, item) => sum + item.amount, 0);
    }
    if (tab === 'cancelPending') {
      return cancelItems
        .filter((item) => selected.has(item._id))
        .reduce((sum, item) => sum + item.amount, 0);
    }
    return stampedRows
      .filter((row) => selected.has(row._id))
      .reduce((sum, row) => sum + stampedAmountOf(row), 0);
  }, [tab, pendingItems, cancelItems, stampedRows, selected]);

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  //* 홈택스에서 처리한 결과를 기록한다. skippedIds는 조용히 삼키지 않고 반드시 알린다 —
  //* 목록을 띄워둔 사이 다른 사람이 먼저 처리했거나 주문이 취소됐을 수 있다.
  const stamp = async (
    ids: string[],
    status: ShopOrderCashReceiptStatus | null,
    actionLabel: string,
  ) => {
    if (working) return;
    if (ids.length === 0) return;
    if (ids.length > BULK_MAX) {
      toastError(
        `한 번에 ${BULK_MAX}건까지만 처리할 수 있습니다. (선택 ${ids.length}건)`,
      );
      return;
    }
    setWorking(true);
    try {
      const result = await bulkUpdateShopOrderCashReceipt(ids, status);
      if (result.modifiedCount > 0) {
        toastSuccess(`${result.modifiedCount}건 ${actionLabel} 처리했습니다.`);
      }
      if (result.skippedIds.length > 0) {
        const numbers = result.skippedIds
          .slice(0, 5)
          .map((id) => `#${id.slice(-6)}`)
          .join(', ');
        toastError(
          `${result.skippedIds.length}건은 건너뛰었습니다 (${numbers}${
            result.skippedIds.length > 5 ? ' 외' : ''
          }) — 이미 같은 상태이거나 대상이 아닙니다.`,
          { duration: 8000 },
        );
      }
      if (result.modifiedCount === 0 && result.skippedIds.length === 0) {
        toastError('처리된 건이 없습니다.');
      }
      setSelected(new Set());
      reload();
    } catch (err) {
      console.error(err);
      toastError(errorMessage(err));
    } finally {
      setWorking(false);
    }
  };

  const confirmStamp = (
    ids: string[],
    status: ShopOrderCashReceiptStatus | null,
    actionLabel: string,
    question: string,
  ) => {
    if (!window.confirm(`${ids.length}건 — ${question}`)) return;
    stamp(ids, status, actionLabel);
  };

  const handleExport = () => {
    if (!pending) return;
    if (pendingItems.length === 0) {
      toastError('내보낼 건이 없습니다.');
      return;
    }
    exportCashReceiptQueueExcel(pendingItems, {
      range: pending.range,
      safeDeadlineDays: thresholds.safeDays,
      maxDeadlineDays: thresholds.limitDays,
    });
    //* 내보내는 건 지금 보고 있는 페이지뿐이다 — 조용히 잘리면 "다 받았다"로 읽힌다
    if (pending.total > pendingItems.length) {
      toastSuccess(
        `${pendingItems.length}건 내보냈습니다. (기간 전체 ${pending.total}건 중 이 페이지 분량 — 달을 좁혀 받아주세요)`,
        { duration: 8000 },
      );
    }
  };

  if (!admin) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-sm text-gray-500">
          관리자 전용 화면입니다.
        </div>
      </div>
    );
  }

  const totalPages = pending ? Math.max(1, Math.ceil(pending.total / pending.limit)) : 1;

  return (
    // 열이 9개까지 늘어 max-w-6xl로는 표가 짜부라진다 (CS 응대 로그와 같은 이유)
    <div className="mx-auto max-w-screen-2xl px-4 pb-24 sm:px-6 lg:px-8">
      {/* 안내 — 내부 화면이라 키워드 단문. 돈이 갈리는 것(차액만·기한)만 강조 */}
      <div className="mt-6 rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-2.5 text-xs leading-relaxed text-cyan-900">
        <p className="break-keep">
          <b>계좌 입금 현금영수증</b> · 홈택스 발행·취소는 사람이, 여기는 대기 목록과
          도장만
        </p>
        <p className="mt-0.5 break-keep">
          기한 안전 {thresholds.safeDays}일 / 최대 {thresholds.limitDays}일 · 오래된
          것부터 정렬 · 대상 <b>계좌입금</b>(전액) / <b>교체차액</b>(<b>차액만</b> —
          결제총액 치면 이중발행)
        </p>
        <p className="mt-0.5 break-keep text-cyan-700">
          면세계산서 발행 건 제외 · 요청 대기는 <b>계산서 요청</b> 배지 (발행 보류)
          {pending?.cutoff && ` · ${fmtDateFull(pending.cutoff)} 이전 결제 제외`}
          {pending?.range.clamped && ' (조회 시작일 조정됨)'}
        </p>
      </div>

      {/* 탭 */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {(
          [
            ['pending', `발행 대기${pending ? ` (${pending.total})` : ''}`],
            ['cancelPending', `취소 대기${cancelQueue ? ` (${cancelQueue.total})` : ''}`],
            ['stamped', `처리 기록${stamped ? ` (${stamped.length})` : ''}`],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`rounded-full border px-4 py-1.5 text-sm ${
              tab === key
                ? 'border-gray-800 bg-gray-800 font-medium text-white'
                : //* 취소 대기가 남아있으면 탭 자체가 눈에 띄어야 한다 — 방치되면
                //* 돈은 돌려주고 매출은 홈택스에 남아 부가세 신고가 어긋난다
                key === 'cancelPending' && (cancelQueue?.total ?? 0) > 0
                ? 'border-red-300 bg-red-50 font-medium text-red-700 hover:bg-red-100'
                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {tab === 'pending' && (
            <button
              type="button"
              onClick={handleExport}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
            >
              엑셀 내려받기
            </button>
          )}
          <button
            type="button"
            onClick={reload}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            새로고침
          </button>
        </div>
      </div>

      {/* 기간 — 취소 대기 큐는 기간 파라미터가 없어(취소는 즉시 처리 대상) 숨긴다 */}
      <div
        className={`mt-3 flex flex-wrap items-center gap-1.5 ${
          tab === 'cancelPending' ? 'hidden' : ''
        }`}
      >
        {(
          [
            ['전체', {}],
            ['이번 달', { from: kstMonthKey(0) }],
            ['지난달', { from: kstMonthKey(-1), to: kstMonthKey(-1) }],
          ] as [string, RangeState][]
        ).map(([label, next]) => {
          const active = range.from === next.from && range.to === next.to;
          return (
            <button
              key={label}
              type="button"
              onClick={() => applyRange(next)}
              className={`rounded-full border px-3 py-1 text-xs ${
                active
                  ? 'border-gray-700 bg-gray-700 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          );
        })}
        {/* 월 선택 — input[type=month]는 로케일 따라 '2026-08'로 보여서 직접 만든다 */}
        <select
          value={range.from && range.from === range.to ? range.from : ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value) applyRange({ from: value, to: value });
            else applyRange({});
          }}
          className="rounded-lg border px-2 py-1 text-xs text-gray-600"
        >
          <option value="">월 선택</option>
          {monthOptions(pending?.cutoff).map((month) => (
            <option key={month} value={month}>
              {monthLabel(month)}
            </option>
          ))}
        </select>
        {/* 지금 무슨 기간을 보고 있는지 한 줄로 — 칩만으로는 '전체'의 범위가 안 보인다 */}
        {pending?.range && (
          <span className="text-xs text-gray-500">
            {fmtDateFull(pending.range.from)} ~ {fmtDateFull(pending.range.to)}
          </span>
        )}
      </div>

      {error && (
        <p className="mt-3 break-keep rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
          {error}
        </p>
      )}

      {tab === 'pending' ? (
        <PendingTab
          data={pending}
          items={pendingItems}
          thresholds={thresholds}
          loading={loading}
          deadlineFilter={deadlineFilter}
          setDeadlineFilter={setDeadlineFilter}
          onMonthClick={(month) => applyRange({ from: month, to: month })}
          selected={selected}
          toggleOne={toggleOne}
          toggleAll={toggleAll}
          allVisibleSelected={allVisibleSelected}
          onStampOne={(id) =>
            confirmStamp(
              [id],
              ShopOrderCashReceiptStatus.Issued,
              '발행 완료',
              '홈택스 발행을 마쳤습니까? 발행 완료로 기록합니다.',
            )
          }
          page={page}
          totalPages={totalPages}
          setPage={setPage}
        />
      ) : tab === 'cancelPending' ? (
        <CancelPendingTab
          data={cancelQueue}
          items={cancelItems}
          loading={loading}
          selected={selected}
          toggleOne={toggleOne}
          toggleAll={toggleAll}
          allVisibleSelected={allVisibleSelected}
          onStampOne={(id) =>
            confirmStamp(
              [id],
              ShopOrderCashReceiptStatus.Canceled,
              '취소 완료',
              '홈택스에서 이 건의 현금영수증을 지웠습니까? 취소 완료로 기록합니다.',
            )
          }
        />
      ) : (
        <StampedTab
          rows={stampedRows}
          allRows={stamped ?? []}
          loading={loading}
          filter={stampedFilter}
          setFilter={setStampedFilter}
          selected={selected}
          toggleOne={toggleOne}
          toggleAll={toggleAll}
          allVisibleSelected={allVisibleSelected}
        />
      )}

      {/* 일괄 도장 바 — 선택이 있을 때만. 금액 합계는 항상 화면에 보이는 선택분과 일치한다 */}
      {selected.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-2px_12px_rgba(0,0,0,0.08)] backdrop-blur lg:pl-64">
          <div className="mx-auto flex max-w-screen-2xl flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-800">
              {selected.size}건 선택
              <span className="ml-2 text-gray-500">
                합계 {formatPrice(selectedAmount)}원
              </span>
            </span>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-xs text-gray-500 underline hover:text-gray-700"
            >
              선택 해제
            </button>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {tab === 'pending' ? (
                <button
                  type="button"
                  disabled={working}
                  onClick={() =>
                    confirmStamp(
                      Array.from(selected),
                      ShopOrderCashReceiptStatus.Issued,
                      '발행 완료',
                      '홈택스 발행을 마친 건입니까? 발행 완료로 기록합니다.',
                    )
                  }
                  className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
                >
                  {working ? '처리 중...' : '발행 완료 도장'}
                </button>
              ) : tab === 'cancelPending' ? (
                //* 취소 대기에선 할 일이 하나뿐이다 — 홈택스에서 지우고 도장.
                //* 잘못 들어온 건 되돌리기는 처리 기록 탭에 있다(여기서 $unset 하면
                //* 취소된 주문이 '발행 대기'로 되살아나 더 위험하다).
                <button
                  type="button"
                  disabled={working}
                  onClick={() =>
                    confirmStamp(
                      Array.from(selected),
                      ShopOrderCashReceiptStatus.Canceled,
                      '취소 완료',
                      '홈택스에서 현금영수증을 지웠습니까? 취소 완료로 기록합니다.',
                    )
                  }
                  className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900 disabled:opacity-50"
                >
                  {working ? '처리 중...' : '취소 완료 도장'}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={working}
                    onClick={() =>
                      confirmStamp(
                        Array.from(selected),
                        ShopOrderCashReceiptStatus.CancelRequested,
                        '취소 요청',
                        '홈택스에서 취소하기로 확정한 건입니까? (아직 취소 전)',
                      )
                    }
                    className="rounded-lg border border-amber-500 bg-white px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                  >
                    취소 요청 도장
                  </button>
                  <button
                    type="button"
                    disabled={working}
                    onClick={() =>
                      confirmStamp(
                        Array.from(selected),
                        ShopOrderCashReceiptStatus.Canceled,
                        '취소 완료',
                        '홈택스에서 취소를 마쳤습니까? 취소 완료로 기록합니다.',
                      )
                    }
                    className="rounded-lg bg-gray-800 px-3 py-2 text-sm font-medium text-white hover:bg-gray-900 disabled:opacity-50"
                  >
                    취소 완료 도장
                  </button>
                  <button
                    type="button"
                    disabled={working}
                    onClick={() =>
                      confirmStamp(
                        Array.from(selected),
                        null,
                        '도장 되돌리기',
                        '기록을 지워 발행 대기로 되돌립니다. 홈택스 처리 자체가 취소되는 것은 아닙니다.',
                      )
                    }
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    도장 되돌리기
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────── 발행 대기 ─────────────────────────── */

interface PendingTabProps {
  data: ShopOrderCashReceiptPendingResponse | null;
  items: ShopOrderCashReceiptPendingItem[];
  thresholds: { safeDays: number; limitDays: number };
  loading: boolean;
  deadlineFilter: 'all' | DeadlineLevel;
  setDeadlineFilter: (f: 'all' | DeadlineLevel) => void;
  onMonthClick: (month: string) => void;
  selected: Set<string>;
  toggleOne: (id: string) => void;
  toggleAll: () => void;
  allVisibleSelected: boolean;
  onStampOne: (id: string) => void;
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
}

const PendingTab: FC<PendingTabProps> = function PendingTab({
  data,
  items,
  thresholds,
  loading,
  deadlineFilter,
  setDeadlineFilter,
  onMonthClick,
  selected,
  toggleOne,
  toggleAll,
  allVisibleSelected,
  onStampOne,
  page,
  totalPages,
  setPage,
}: PendingTabProps) {
  //* 구간별 건수는 **서버가 기간 전체로** 세서 준다. 구버전 응답이면(값 없음) 페이지분만
  //* 세는 폴백으로 내려가고, 그때만 "이 페이지 기준"이라고 밝힌다 — 조용히 축소하지 않는다.
  const serverCounted = data?.safeCount !== undefined;
  //* 페이지에 실제로 실려온 건수 — 응답이 items를 빠뜨려도 문구가 죽지 않게 한 번만 센다
  const pageItemCount = data?.items?.length ?? 0;
  const buckets = useMemo(() => {
    if (data && data.safeCount !== undefined) {
      return {
        safe: data.safeCount,
        late: data.lateCount ?? 0,
        over: data.overCount ?? 0,
      };
    }
    const base = { safe: 0, late: 0, over: 0 };
    (data?.items ?? []).forEach((item) => {
      base[deadlineOf(item.waitingDays, thresholds, item.tier).level] += 1;
    });
    return base;
  }, [data, thresholds]);
  const pageCounted = !serverCounted && !!data && data.total > pageItemCount;

  return (
    <div className="mt-4 space-y-3">
      {/* 기간 전체 요약 (페이지 아님) */}
      {data && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg bg-white px-4 py-3 shadow">
          <div>
            <div className="text-[11px] text-gray-400">대기 건수</div>
            <div className="text-lg font-semibold text-gray-800">{data.total}건</div>
          </div>
          <div>
            <div className="text-[11px] text-gray-400">발행할 금액</div>
            <div className="text-lg font-semibold text-gray-800">
              {formatPrice(data.totalAmount)}원
            </div>
          </div>
          <div className="border-l pl-4">
            <div className="text-[11px] text-gray-400">
              최대기한({thresholds.limitDays}일) 초과
            </div>
            <div
              className={`text-lg font-semibold ${
                buckets.over > 0 ? 'text-red-600' : 'text-gray-800'
              }`}
            >
              {buckets.over}건
            </div>
          </div>
          <div>
            <div className="text-[11px] text-gray-400">
              안전기한({thresholds.safeDays}일) 초과
            </div>
            <div
              className={`text-lg font-semibold ${
                buckets.late > 0 ? 'text-amber-600' : 'text-gray-800'
              }`}
            >
              {buckets.late}건
            </div>
          </div>
          <div>
            <div className="text-[11px] text-gray-400">기한 내</div>
            <div className="text-lg font-semibold text-gray-800">{buckets.safe}건</div>
          </div>
          {pageCounted && (
            <span className="text-[11px] text-gray-400">
              ※ 기한 구간은 이 페이지({pageItemCount}건) 기준
            </span>
          )}
        </div>
      )}

      {/* 기한 구간 필터 + 월별 집계 */}
      {data && (
        <div className="flex flex-wrap items-center gap-1.5">
          {(
            [
              ['all', `전체 (${serverCounted ? data.total : pageItemCount})`],
              ['over', `${DEADLINE_FILTER_LABEL.over} (${buckets.over})`],
              ['late', `${DEADLINE_FILTER_LABEL.late} (${buckets.late})`],
              ['safe', `${DEADLINE_FILTER_LABEL.safe} (${buckets.safe})`],
            ] as ['all' | DeadlineLevel, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setDeadlineFilter(key)}
              className={`rounded-full border px-3 py-1 text-xs ${
                deadlineFilter === key
                  ? 'border-gray-700 bg-gray-700 text-white'
                  : key === 'over' && buckets.over > 0
                  ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                  : key === 'late' && buckets.late > 0
                  ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
          {/* 월별 집계 — 눌러서 그 달만 좁혀본다 (한 달치가 한 페이지에 들어간다) */}
          {data.byMonth.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5 border-l pl-3">
              {data.byMonth.map((row) => (
                <button
                  key={row.month}
                  type="button"
                  onClick={() => onMonthClick(row.month)}
                  className="rounded-full border border-gray-300 px-2.5 py-1 text-[11px] text-gray-600 hover:bg-gray-50"
                >
                  {monthLabel(row.month)} {row.count}건 · {formatPrice(row.amount)}원
                  {row.over > 0 && (
                    <span className="ml-1 font-semibold text-red-600">
                      초과 {row.over}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-sm text-gray-400">
          {deadlineFilter === 'all'
            ? '발행 대기 중인 건이 없습니다.'
            : `${DEADLINE_FILTER_LABEL[deadlineFilter]}에 해당하는 건이 없습니다.`}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="w-full min-w-[1180px] text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                </th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">결제일</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">발행 기한</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">구분</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">주문</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">주문자</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">전화번호</th>
                <th className="px-3 py-3 font-medium">상품</th>
                <th className="whitespace-nowrap px-3 py-3 text-right font-medium">
                  발행 금액
                </th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">비고</th>
                <th className="w-24 px-3 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => {
                const checked = selected.has(item._id);
                const deadline = deadlineOf(item.waitingDays, thresholds, item.tier);
                const kind = KIND_BADGE[item.kind] ?? KIND_BADGE.deposit;
                return (
                  <tr
                    key={item._id}
                    className={
                      checked
                        ? 'bg-emerald-50/60'
                        : deadline.rowClassName || 'hover:bg-gray-50'
                    }
                  >
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOne(item._id)}
                        className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                      {fmtDate(item.purchasedAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      <span
                        className={`inline-flex rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-tight ${deadline.className}`}
                        title={`안전기한 ${fmtDateFull(
                          deadlineDate(item.purchasedAt, thresholds.safeDays),
                        )} · 최대기한 ${fmtDateFull(
                          deadlineDate(item.purchasedAt, thresholds.limitDays),
                        )}`}
                      >
                        {deadline.label}
                      </span>
                      <div className="mt-0.5 text-[10px] text-gray-400">
                        {deadline.sub}
                      </div>
                    </td>
                    {/* 발행액이 결제 전액인지 교체 차액인지 — 홈택스에 칠 금액이 갈린다 */}
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      <span
                        className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${kind.className}`}
                      >
                        {kind.label}
                      </span>
                      {item.kind === 'itemChange' && item.lastChangedAt && (
                        <div className="mt-0.5 text-[10px] text-gray-400">
                          교체 {fmtDate(item.lastChangedAt)}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-400">
                      #{item.orderNo}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs font-medium text-gray-800">
                      {item.senderName || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      {item.senderPhoneNumber ? (
                        <button
                          type="button"
                          onClick={() => copyPhone(item.senderPhoneNumber ?? '')}
                          className="rounded font-medium text-gray-700 underline decoration-dotted hover:text-cyan-700"
                          title="복사 (홈택스 식별정보)"
                        >
                          {phoneNumberStringFormator(item.senderPhoneNumber)}
                        </button>
                      ) : (
                        <span className="text-red-500">번호 없음</span>
                      )}
                    </td>
                    <td className="min-w-[180px] break-keep px-3 py-2.5 text-xs text-gray-600">
                      {item.itemName || '-'}
                      {item.quantity && item.quantity > 1 && (
                        <span className="ml-1 text-gray-400">×{item.quantity}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      <div className="font-semibold text-gray-900">
                        {formatPrice(item.amount)}원
                      </div>
                      {/*! 교체차액 건은 결제총액이 따로 있다. 이걸 안 보여주면 대사 때
                          "금액이 왜 다르지"가 되고, 크게 보여주면 그걸 홈택스에 쳐서
                          이중 발행이 난다 — 작게, '차액만'을 붙여서 병기한다 */}
                      {item.kind === 'itemChange' ? (
                        <div className="text-[10px] text-violet-600">
                          차액만 · 결제총액 {formatPrice(item.paymentByCash)}원
                        </div>
                      ) : (
                        /* 적립금은 현금이 아니라 발행 대상이 아니다 — 대사용 참고값 */
                        item.paymentByPoint > 0 && (
                          <div className="text-[10px] text-gray-400">
                            적립금 {formatPrice(item.paymentByPoint)}P 별도
                          </div>
                        )
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      <div className="flex flex-wrap items-center gap-1">
                        {item.paymentStatus && (
                          <span
                            className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                              paymentStatusBadge(item.paymentStatus).className
                            }`}
                          >
                            {paymentStatusBadge(item.paymentStatus).label}
                          </span>
                        )}
                        {/*! 계산서 요청이 대기 중인 건 — 발행하면 곧 취소해야 한다.
                            발행 완료(Done)된 건은 아예 목록에 오지 않는다 */}
                        {item.taxInvoiceStatus === 'Requested' && (
                          <span
                            className="inline-flex rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-red-700"
                            title="면세계산서 요청 대기 · 발행 보류(발행 시 취소 필요)"
                          >
                            계산서 요청
                          </span>
                        )}
                        {/* 금액이 움직인 건 — 발행액을 한 번 더 확인하라는 표식 */}
                        {item.hasItemChange && (
                          <span
                            className="inline-flex rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-amber-800"
                            title={item.paymentMemo ?? '상품 교체로 금액이 변경된 건'}
                          >
                            상품교체
                          </span>
                        )}
                      </div>
                      {item.paymentMemo && (
                        <div
                          className="mt-0.5 max-w-[220px] truncate text-[10px] text-gray-400"
                          title={item.paymentMemo}
                        >
                          {item.paymentMemo}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => onStampOne(item._id)}
                        className="rounded-lg border border-emerald-600 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                      >
                        발행 완료
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1 text-gray-600 disabled:opacity-40"
          >
            이전
          </button>
          <span className="text-gray-500">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1 text-gray-600 disabled:opacity-40"
          >
            다음
          </button>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────── 취소 대기 ─────────────────────────── */

interface CancelPendingTabProps {
  data: ShopOrderCashReceiptCancelPendingResponse | null;
  items: ShopOrderCashReceiptCancelPendingItem[];
  loading: boolean;
  selected: Set<string>;
  toggleOne: (id: string) => void;
  toggleAll: () => void;
  allVisibleSelected: boolean;
  onStampOne: (id: string) => void;
}

/**
 * 취소 대기 큐 — **프론트가 만드는 목록이 아니다.** 주문 취소가 확정되면 서버가 그 주문의
 * `Issued` 도장을 `CancelRequested`로 자동 전환해 여기 쌓는다.
 *
 * 기간 필터도 하한선도 없다(취소는 언제 결제된 건이든 즉시 처리 대상). 정렬은 오래 기다린
 * 것부터(서버 고정)이고, `waitingDays`는 결제일이 아니라 **취소 대기로 넘어온 시각** 기준이다.
 * 여기서 할 일은 홈택스에서 지우고 `Canceled` 도장을 찍는 것 하나뿐.
 */
const CancelPendingTab: FC<CancelPendingTabProps> = function CancelPendingTab({
  data,
  items,
  loading,
  selected,
  toggleOne,
  toggleAll,
  allVisibleSelected,
  onStampOne,
}: CancelPendingTabProps) {
  return (
    <div className="mt-4 space-y-3">
      <div className="break-keep rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900">
        취소 확정 건 자동 적재 · <b>환불은 됐는데 홈택스엔 매출 잔존</b> — 홈택스에서 지운
        뒤 취소 완료 도장 · 기간 필터 없음(전량)
      </div>

      {data && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg bg-white px-4 py-3 shadow">
          <div>
            <div className="text-[11px] text-gray-400">취소 대기</div>
            <div
              className={`text-lg font-semibold ${
                data.total > 0 ? 'text-red-600' : 'text-gray-800'
              }`}
            >
              {data.total}건
            </div>
          </div>
          <div>
            <div className="text-[11px] text-gray-400">취소할 금액</div>
            <div className="text-lg font-semibold text-gray-800">
              {formatPrice(data.totalAmount)}원
            </div>
          </div>
          {/* 한 페이지에 다 안 들어왔으면 밝힌다 — 조용히 잘리면 "이게 전부"로 읽힌다 */}
          {data.total > items.length && (
            <span className="text-[11px] text-gray-400">
              ※ {data.total}건 중 {items.length}건 표시 (오래 기다린 순)
            </span>
          )}
        </div>
      )}

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-sm text-gray-400">
          취소 대기 중인 건이 없습니다.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="w-full min-w-[1180px] text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                </th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">취소 접수</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">구분</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">주문</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">결제일</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">주문자</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">전화번호</th>
                <th className="px-3 py-3 font-medium">상품</th>
                <th className="whitespace-nowrap px-3 py-3 text-right font-medium">
                  취소 금액
                </th>
                <th className="px-3 py-3 font-medium">취소 사유</th>
                <th className="w-24 px-3 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => {
                const checked = selected.has(item._id);
                return (
                  <tr
                    key={item._id}
                    className={checked ? 'bg-emerald-50/60' : 'hover:bg-gray-50'}
                  >
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOne(item._id)}
                        className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      <div className="font-medium text-gray-700">
                        {fmtDate(item.requestedAt)}
                      </div>
                      {/* 기준이 결제일이 아니라 취소 대기로 넘어온 시각이다 */}
                      <div className="text-[10px] text-gray-400">
                        {/* 결제일이 아니라 취소 대기로 넘어온 뒤 지난 일수다 */}
                        {item.waitingDaysSinceRequest === 0
                          ? '오늘'
                          : `${item.waitingDaysSinceRequest}일 대기`}
                        {/* 실행자가 없으면 시스템(웹훅)이 내린 것 — 이름은 못 띄운다 */}
                        <span className="ml-1">
                          · {item.requestedBy ? '관리자' : '시스템'}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      <span
                        className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                          (KIND_BADGE[item.kind] ?? KIND_BADGE.deposit).className
                        }`}
                      >
                        {(KIND_BADGE[item.kind] ?? KIND_BADGE.deposit).label}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-400">
                      #{item.orderNo}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                      {fmtDate(item.purchasedAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs font-medium text-gray-800">
                      {item.senderName || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      {item.senderPhoneNumber ? (
                        <button
                          type="button"
                          onClick={() => copyPhone(item.senderPhoneNumber ?? '')}
                          className="rounded font-medium text-gray-700 underline decoration-dotted hover:text-cyan-700"
                          title="복사 (홈택스 식별정보)"
                        >
                          {phoneNumberStringFormator(item.senderPhoneNumber)}
                        </button>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="min-w-[180px] break-keep px-3 py-2.5 text-xs text-gray-600">
                      {item.itemName || '-'}
                      {item.quantity && item.quantity > 1 && (
                        <span className="ml-1 text-gray-400">×{item.quantity}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      <div className="font-semibold text-gray-900">
                        {formatPrice(item.amount)}원
                      </div>
                      {item.kind === 'itemChange' ? (
                        <div className="text-[10px] text-violet-600">
                          차액만 · 결제총액 {formatPrice(item.paymentByCash)}원
                        </div>
                      ) : (
                        item.hasItemChange && (
                          <div
                            className="text-[10px] text-amber-600"
                            title={item.paymentMemo ?? '상품 교체로 금액이 변경된 건'}
                          >
                            상품교체
                          </div>
                        )
                      )}
                    </td>
                    <td className="min-w-[160px] break-keep px-3 py-2.5 text-xs text-gray-500">
                      {item.cancelReason || '-'}
                      {item.canceledAt && (
                        <div className="text-[10px] text-gray-400">
                          주문취소 {fmtDate(item.canceledAt)}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => onStampOne(item._id)}
                        className="rounded-lg border border-gray-700 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                      >
                        취소 완료
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────── 처리 기록 ─────────────────────────── */

interface StampedTabProps {
  rows: ShopOrderCashReceiptStampedRow[];
  allRows: ShopOrderCashReceiptStampedRow[];
  loading: boolean;
  filter: StampedFilter;
  setFilter: (f: StampedFilter) => void;
  selected: Set<string>;
  toggleOne: (id: string) => void;
  toggleAll: () => void;
  allVisibleSelected: boolean;
}

const StampedTab: FC<StampedTabProps> = function StampedTab({
  rows,
  allRows,
  loading,
  filter,
  setFilter,
  selected,
  toggleOne,
  toggleAll,
  allVisibleSelected,
}: StampedTabProps) {
  const counts = useMemo(() => {
    const base = {
      all: allRows.length,
      [ShopOrderCashReceiptStatus.Issued]: 0,
      [ShopOrderCashReceiptStatus.CancelRequested]: 0,
      [ShopOrderCashReceiptStatus.Canceled]: 0,
      cancelNeeded: 0,
    };
    allRows.forEach((row) => {
      const status = row.selfCashReceiptDetail?.status;
      if (!status) return;
      base[status] += 1;
      if (
        status === ShopOrderCashReceiptStatus.Issued &&
        isOrderCanceled(row.paymentDetail?.status)
      ) {
        base.cancelNeeded += 1;
      }
    });
    return base;
  }, [allRows]);

  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {(
          [
            ['all', '전체'],
            [ShopOrderCashReceiptStatus.Issued, '발행완료'],
            [ShopOrderCashReceiptStatus.CancelRequested, '취소요청'],
            [ShopOrderCashReceiptStatus.Canceled, '취소완료'],
            ['cancelNeeded', '전환 누락'],
          ] as [StampedFilter, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`rounded-full border px-3 py-1 text-xs ${
              filter === key
                ? 'border-gray-700 bg-gray-700 text-white'
                : key === 'cancelNeeded' && counts.cancelNeeded > 0
                ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {label} ({counts[key]})
          </button>
        ))}
      </div>

      {/* 자동 전환이 정상이면 0이다 — 0이 아니면 그물에 걸린 것이니 이유까지 알려준다 */}
      {counts.cancelNeeded > 0 && (
        <p className="break-keep rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          발행 도장 ∧ 주문 취소 {counts.cancelNeeded}건 — <b>자동 전환 누락</b>(취소 대기
          큐에 안 뜸) · 취소 요청 도장을 찍으면 큐로 넘어감
        </p>
      )}

      {loading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-sm text-gray-400">
          해당 조건의 처리 기록이 없습니다.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="w-full min-w-[1180px] text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                </th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">현금영수증</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">구분</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">처리 일시</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">처리자</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">결제일</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">주문</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">주문자</th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">전화번호</th>
                <th className="px-3 py-3 font-medium">상품</th>
                <th className="whitespace-nowrap px-3 py-3 text-right font-medium">
                  발행 금액
                </th>
                <th className="whitespace-nowrap px-3 py-3 font-medium">주문 상태</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => {
                const detail = row.selfCashReceiptDetail;
                const badge = detail
                  ? CASH_RECEIPT_STATUS_BADGE[detail.status]
                  : undefined;
                const canceled = isOrderCanceled(row.paymentDetail?.status);
                const cancelNeeded =
                  detail?.status === ShopOrderCashReceiptStatus.Issued && canceled;
                const checked = selected.has(row._id);
                const phone = row.deliveryDetail?.senderPhoneNumber;
                const kind = stampedKindOf(row);
                const amount = stampedAmountOf(row);
                return (
                  <tr
                    key={row._id}
                    className={
                      checked
                        ? 'bg-emerald-50/60'
                        : cancelNeeded
                        ? 'bg-red-50/40'
                        : 'hover:bg-gray-50'
                    }
                  >
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOne(row._id)}
                        className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5">
                      {badge && (
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold leading-tight ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      )}
                      {cancelNeeded && (
                        <div className="mt-0.5 text-[10px] font-semibold text-red-600">
                          전환 누락
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      <span
                        className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${KIND_BADGE[kind].className}`}
                      >
                        {KIND_BADGE[kind].label}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500">
                      {fmtDateTime(detail?.statusAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                      {userNameOf(detail?.statusBy) ?? '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                      {fmtDate(row.paymentDetail?.purchasedAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-400">
                      #{row._id.slice(-6)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs font-medium text-gray-800">
                      {row.deliveryDetail?.senderName || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      {phone ? (
                        <button
                          type="button"
                          onClick={() => copyPhone(phone)}
                          className="rounded text-gray-700 underline decoration-dotted hover:text-cyan-700"
                          title="복사"
                        >
                          {phoneNumberStringFormator(phone)}
                        </button>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="min-w-[180px] break-keep px-3 py-2.5 text-xs text-gray-600">
                      {row.orderDetail?.shopItemEmbed?.name || '-'}
                      {row.orderDetail?.quantity && row.orderDetail.quantity > 1 && (
                        <span className="ml-1 text-gray-400">
                          ×{row.orderDetail.quantity}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      <div className="font-semibold text-gray-900">
                        {formatPrice(amount)}원
                      </div>
                      {kind === 'itemChange' && (
                        <div className="text-[10px] text-violet-600">
                          차액만 · 결제총액{' '}
                          {formatPrice(row.paymentDetail?.paymentByCash ?? 0)}원
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      <span
                        className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                          paymentStatusBadge(row.paymentDetail?.status).className
                        }`}
                      >
                        {paymentStatusBadge(row.paymentDetail?.status).label}
                      </span>
                      {row.paymentDetail?.canceledAt && (
                        <div className="mt-0.5 text-[10px] text-gray-400">
                          {fmtDate(row.paymentDetail.canceledAt)}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
